import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, X, Eye, EyeOff, GripVertical } from 'lucide-react';

const COLOR_OPTIONS = [
  { value: 'bg-black text-white', label: 'Schwarz', preview: 'bg-black' },
  { value: 'bg-blue-600 text-white', label: 'Blau', preview: 'bg-blue-600' },
  { value: 'bg-green-600 text-white', label: 'Grün', preview: 'bg-green-600' },
  { value: 'bg-red-600 text-white', label: 'Rot', preview: 'bg-red-600' },
  { value: 'bg-yellow-500 text-black', label: 'Gelb', preview: 'bg-yellow-500' },
  { value: 'bg-purple-600 text-white', label: 'Lila', preview: 'bg-purple-600' },
  { value: 'bg-gray-800 text-white', label: 'Grau', preview: 'bg-gray-800' },
];

const AdminBanners = () => {
  const { user } = useAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    text: '',
    color: 'bg-black text-white',
    link: '',
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const response = await api.get('/admin/banners');
      setBanners(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Banner');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.text.trim()) {
      toast.error('Banner-Text ist erforderlich');
      return;
    }

    try {
      if (editingBanner) {
        await api.put(`/admin/banners/${editingBanner.id}`, formData);
        toast.success('Banner aktualisiert');
      } else {
        await api.post('/admin/banners', formData);
        toast.success('Banner erstellt');
      }
      loadBanners();
      resetForm();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Banner wirklich löschen?')) return;
    try {
      await api.delete(`/admin/banners/${id}`);
      toast.success('Banner gelöscht');
      loadBanners();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleActive = async (banner) => {
    try {
      await api.put(`/admin/banners/${banner.id}`, { is_active: !banner.is_active });
      loadBanners();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const resetForm = () => {
    setFormData({
      text: '',
      color: 'bg-black text-white',
      link: '',
      is_active: true,
      sort_order: banners.length
    });
    setEditingBanner(null);
    setShowAddForm(false);
  };

  const startEdit = (banner) => {
    setFormData({
      text: banner.text,
      color: banner.color,
      link: banner.link || '',
      is_active: banner.is_active,
      sort_order: banner.sort_order
    });
    setEditingBanner(banner);
    setShowAddForm(true);
  };

  if (loading) return <div className="p-8 text-center">Laden...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="admin-banners-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Banner-Verwaltung</h1>
          <p className="text-gray-500 text-sm mt-1">
            Verwalten Sie die Aktionsbanner auf der Startseite
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
          data-testid="add-banner-btn"
        >
          <Plus className="w-4 h-4" />
          Neues Banner
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-gray-50 rounded-xl p-6 mb-6" data-testid="banner-form">
          <h2 className="font-bold mb-4">{editingBanner ? 'Banner bearbeiten' : 'Neues Banner'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Banner-Text *</label>
              <input
                type="text"
                value={formData.text}
                onChange={(e) => setFormData({...formData, text: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="🚀 KOSTENLOSER VERSAND ab 150€"
                required
                data-testid="banner-text-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Farbe</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({...formData, color: option.value})}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${option.value} ${
                      formData.color === option.value ? 'ring-2 ring-offset-2 ring-black' : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Link (optional)</label>
              <input
                type="text"
                value={formData.link}
                onChange={(e) => setFormData({...formData, link: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="/panasonic/profi-haarschneidemaschinen-zubehoer"
                data-testid="banner-link-input"
              />
              <p className="text-xs text-gray-500 mt-1">Klick auf Banner führt zu dieser URL</p>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                <span className="text-sm">Aktiv</span>
              </label>
              
              <div className="flex items-center gap-2">
                <label className="text-sm">Reihenfolge:</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                  className="w-20 border rounded px-2 py-1"
                  min="0"
                />
              </div>
            </div>
            
            {/* Preview */}
            <div>
              <label className="block text-sm font-medium mb-1">Vorschau:</label>
              <div className={`w-full py-3 text-center rounded-lg ${formData.color}`}>
                <p className="text-sm md:text-base font-medium px-4">
                  {formData.text || 'Banner-Text hier...'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800" data-testid="save-banner-btn">
                <Save className="w-4 h-4 inline mr-2" />
                Speichern
              </button>
              <button type="button" onClick={resetForm} className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300">
                <X className="w-4 h-4 inline mr-2" />
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Banners List */}
      <div className="space-y-3">
        {banners.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Noch keine Banner erstellt</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-2 text-blue-600 hover:underline"
            >
              Erstes Banner erstellen
            </button>
          </div>
        ) : (
          banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`border rounded-lg overflow-hidden ${!banner.is_active ? 'opacity-50' : ''}`}
              data-testid={`banner-item-${banner.id}`}
            >
              {/* Banner Preview */}
              <div className={`py-2 px-4 text-center ${banner.color}`}>
                <p className="text-sm font-medium">{banner.text}</p>
              </div>
              
              {/* Banner Actions */}
              <div className="bg-white px-4 py-3 flex justify-between items-center">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <GripVertical className="w-4 h-4" />
                    #{banner.sort_order}
                  </span>
                  {banner.link && (
                    <span className="truncate max-w-[200px]">→ {banner.link}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(banner)}
                    className={`p-2 rounded ${banner.is_active ? 'text-green-600' : 'text-gray-400'}`}
                    title={banner.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    {banner.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => startEdit(banner)}
                    className="p-2 hover:bg-gray-100 rounded"
                    data-testid={`edit-banner-${banner.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(banner.id)}
                    className="p-2 hover:bg-red-50 text-red-500 rounded"
                    data-testid={`delete-banner-${banner.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Info */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
        <p className="font-medium">💡 Tipps:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Nur 1 Banner? Es wird ohne Rotation angezeigt</li>
          <li>Mehrere Banner rotieren alle 5 Sekunden</li>
          <li>Nutzen Sie Emojis für mehr Aufmerksamkeit (🚀 ⚡ 🎁 ✨)</li>
          <li>Links können zu Kategorien oder Produktseiten führen</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminBanners;
